import React from "react";
import { Box, Chip, styled, Typography } from "@mui/material";
import { RichText } from "../../components/RichText";
import { LadderXpTime } from "./LadderXpTime";
import { SmallGoldIcon } from "./icons/SmallGoldIcon";
import { ResumeIcon } from "./icons/ResumeIcon";
import { ExitButton } from "./ExitButton";
import {
  ActionButton,
  FullScreen,
  LadderScreenTopBar,
  LadderImage,
  LadderStartScreenLayout,
  TopRightLabel,
} from "./styles";
import { LadderType } from "./LadderType";
import { LadderTitle } from "./LadderTitle";
import type { Ladder, LadderProgress } from "../types";

const CheckmarkIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M9.86 18a1 1 0 0 1-.73-.32l-4.86-5.17a1 1 0 1 1 1.46-1.37l4.12 4.39 8.41-9.2a1 1 0 1 1 1.48 1.34l-9.14 10a1 1 0 0 1-.73.33z" />
  </svg>
);

const QuestionIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    style={{ marginRight: 4, width: 16 }}
  >
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z" />
  </svg>
);

const CompletedSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 3,
  margin: 10,
});

const HasGoldContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 3,
  margin: 10,
});

export const QuizStartScreen = ({
  ladder,
  ladderProgress,
  itemCount,
  onClose,
  onCancel,
}: {
  ladder: Ladder;
  ladderProgress: LadderProgress | null;
  itemCount?: number;
  onClose: () => void;
  onCancel: () => void;
}) => {
  const lp = ladderProgress;
  const status = lp?.overallStatus || "NOT_STARTED";

  const hasGold = lp && lp.highScore >= lp.maxScore && lp.maxScore > 0;

  const inProgress = status === "IN_PROGRESS";
  const isCompleted = status === "COMPLETED";
  const isNotStarted = !(inProgress || isCompleted);

  const { estimatedTime, experiencePoints } = ladder;

  const formatCompleteDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      return new Intl.DateTimeFormat("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }).format(date);
    } catch {
      return dateStr;
    }
  };

  return (
    <FullScreen>
      <LadderScreenTopBar>
        <ExitButton onClick={onCancel} />
        <TopRightLabel>
          <Typography variant="body1">
            <LadderType ladder={ladder} />
          </Typography>
        </TopRightLabel>
      </LadderScreenTopBar>

      <LadderStartScreenLayout>
        <LadderTitle ladder={ladder} />

        <LadderXpTime
          experiencePoints={experiencePoints}
          estimatedTime={estimatedTime}
        >
          {itemCount != null && (
            <Typography
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                fontSize: "12px",
              }}
            >
              <QuestionIcon />
              {itemCount} {itemCount === 1 ? "question" : "questions"}
            </Typography>
          )}
        </LadderXpTime>
        <LadderImage src={ladder.image?.url} alt={ladder.image?.alt} />
        <Typography
          variant="body1"
          sx={{ textAlign: "center" }}
          component="div"
        >
          <RichText value={ladder.shortDescription} />
        </Typography>

        {isCompleted && (
          <CompletedSection>
            <Chip label="Completed" color="success" icon={<CheckmarkIcon />} />
            {lp?.completeDate && (
              <Typography
                sx={{ fontStyle: "italic" }}
                variant="h6"
                color="success.dark"
              >
                You completed this ladder on{" "}
                {formatCompleteDate(lp.completeDate)}
              </Typography>
            )}
          </CompletedSection>
        )}

        {hasGold && (
          <HasGoldContainer>
            <SmallGoldIcon width={75} height={75} />
            <Typography color="warning.dark" variant="subtitle1">
              Perfect Score
            </Typography>
          </HasGoldContainer>
        )}

        {isNotStarted && (
          <ActionButton variant="contained" color="primary" onClick={onClose}>
            {(lp?.numberOfSessions ?? 0) > 0 ? "Try Again" : "Start"}
          </ActionButton>
        )}

        {inProgress && (
          <ActionButton variant="contained" color="warning" onClick={onClose}>
            <ResumeIcon style={{ marginRight: 10 }} />
            Resume
          </ActionButton>
        )}

        {isCompleted && (
          <ActionButton variant="contained" color="primary" onClick={onClose}>
            Review
          </ActionButton>
        )}
      </LadderStartScreenLayout>
    </FullScreen>
  );
};
